import { useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import {
  ROUTE_ADMIN_CATALOGS,
  ROUTE_ADMIN_CATALOGS_UPDATE,
  ROUTE_ADMIN_CHANGE_KNO_IN_PLAN,
  ROUTE_ADMIN_PUBLISH_RULES,
  ROUTE_ADMIN_END_CHECKING_ERP,
  ROUTE_ADMIN_EXPORTS,
  ROUTE_ADMIN_INOPERABILITY,
  ROUTE_ADMIN_NEWS,
  ROUTE_ADMIN_NEWSLETTER,
  ROUTE_ADMIN_MANGE_TEST_USER,
  ROUTE_ADMIN_AUTOSAVE,
  ROUTE_ADMIN_USERS,
  ROUTE_CHECK_SHEETS,
  ROUTE_INFO,
  ROUTE_REQUIREMENTS,
  ROUTE_SETTINGS,
  ROUTE_KNM_MASS_CANCELLATION,
  USER_NOTIFICATION,
  ROUTE_KNM_MASS_DELETION,
  ROUTE_ADMIN_PAPERLESS,
  ROUTE_ADMIN_KNO_VALIDATION,
  ROUTE_ADMIN_IMMEDIATE_TEST_PURCHASE,
  ROUTE_ADMIN_HIDE_FIELDS,
  ROUTE_ADMIN_ADDING_PM_ERP,
  ROUTE_ADMIN_CHANGE_KNO_IN_MEASURE,
  ROUTE_ADMIN_SMEV,
} from 'CorePackage/MainModule/routes/routes';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getUserInfo, getUserRole } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IUserInfo } from 'APIPackage/AuthModule/interfaces/IUserInfo';
import { IUserRole } from 'APIPackage/AuthModule/interfaces/IUserRole';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { AdminUsers } from 'PersonalAccountPackage/AdminModule/components/AdminUsers';
import { AdminCatalogs } from 'PersonalAccountPackage/AdminModule/components/AdminCatalogs';
import { AdminCatalogsExports } from 'PersonalAccountPackage/AdminModule/components/AdminCatalogsExports';
import { AdminCatalogsUpdate } from 'PersonalAccountPackage/AdminModule/components/AdminCatalogsUpdate';
import { NewsTable } from 'PersonalAccountPackage/AdminModule/components/NewsTable';
import { SystemInoperabilityTable } from 'PersonalAccountPackage/AdminModule/components/SystemInoperabilityTable';
import { EndCheckingErp } from 'PersonalAccountPackage/AdminModule/components/EndCheckingErp';
import { ManageTestUsersTable } from 'PersonalAccountPackage/AdminModule/components/ManageTestUsersTable';
import { AdminAutosaveSettings } from 'PersonalAccountPackage/AdminModule/components/AdminAutosaveSettings';
import { PublishRulesPage } from 'PersonalAccountPackage/AdminModule/components/PublishRulesPage';
import { AdminSettingsPaperless } from 'PersonalAccountPackage/AdminModule/components/AdminSettingsPaperless';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { AdminHideFieldsFromSubjectForm } from 'PersonalAccountPackage/AdminModule/components/AdminHideFieldsFromSubjectForm';

import { UserSettings } from './UserSettings';
import styles from './PersonalAccount.module.scss';
import { ControlPanel } from './ControlPanel';
import { IMenuItem } from '../interfaces/IMenuItem';
import { UserInfo } from '../../UserInfoModule/components/UserInfo';
import { CheckLists } from '../../CheckListModule/components/CheckLists';
import { UserRequirements } from '../../UserRequirementsModule/components/UserRequirements';
import { ControlOfNewsLettersPage } from '../../AdminModule/components/ControlOfNewsLettersPage';
import { jobMap } from '../common/jobMap';
import { ChangeKnoInPlanForm } from '../../AdminModule/components/ChangeKnoInPlanForm';
import { UserNotificationCount } from '../../../UserNotificationPackage/MainModule/components/UserNotificationCount';
import { UserNotificationForm } from '../../../UserNotificationPackage/MainModule/components/UserNotificationForm';
import { PassportMassCancellation } from '../../PassportMassCancellationModule/components/PassportMassCancellation';
import { passportGroupIsExistsGroupsForCancellationSelector } from '../../../PassportGroupPackage/MainModule/selectors/passportGroupSelector';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { passportGroupGetIsExistsGroupsForCancellationAction } from '../../../PassportGroupPackage/MainModule/actions/passportGroupActions';
import { PassportMassDeletion } from '../../PassportMassDeletionModule/components/PassportMassDeletion';
import { AdminSettingsKnoValidation } from '../../AdminModule/components/AdminSettingsKnoValidation';
import { ImmediateTestPurchaseForm } from './ImmediateTestPurchaseForm';
import { AdminSettingsAddingPmErpForm } from '../../AdminModule/components/AdminSettingsAddingPmErpForm';
import { ChangeKnoInMeasureForm } from '../../AdminModule/components/ChangeKnoInMeasureForm';
import { SmevRequestForm } from '../../../SmevMessagesPackage/MainModule/components/SmevRequestForm';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const notificationMenuItem = {
  route: USER_NOTIFICATION,
  component: <UserNotificationForm />,
  title: (
    <div style={{ display: 'flex' }}>
      Уведомления{'\u00A0'}
      {'\u00A0'}
      {'\u00A0'}
      <UserNotificationCount />
    </div>
  ),
};

const menuItemsBase: IMenuItem[] = [
  {
    route: ROUTE_SETTINGS,
    component: <UserSettings />,
    title: 'Настройки',
    versionAvailable: [LawInterfacesEnum.FZ_248, LawInterfacesEnum.FZ_294],
  },
];

const renderRoutes = (
  isExistsGroupsForCancellation: boolean,
  sysname?: UserRoleEnum,
  lawInterface?: LawInterfacesEnum,
) => {
  switch (sysname) {
    case UserRoleEnum.ADMIN: {
      const menuItemsAdmin: IMenuItem[] = [
        {
          route: ROUTE_ADMIN_USERS,
          component: <AdminUsers />,
          title: 'Управление пользователями',
        },
        {
          route: ROUTE_ADMIN_CATALOGS,
          component: <AdminCatalogs />,
          title: 'Управление справочниками',
        },
        {
          route: ROUTE_ADMIN_EXPORTS,
          component: <AdminCatalogsExports />,
          title: 'Управление расписанием',
        },
        ...(lawInterface !== LawInterfacesEnum.FZ_248
          ? [
              {
                route: ROUTE_ADMIN_ADDING_PM_ERP,
                component: <AdminSettingsAddingPmErpForm />,
                title: 'Добавление ПМ для ЕРП',
              },
            ]
          : []),
        {
          route: ROUTE_ADMIN_PAPERLESS,
          component: <AdminSettingsPaperless />,
          title: 'Включение безбумажного функционала',
        },
        {
          route: ROUTE_ADMIN_KNO_VALIDATION,
          component: <AdminSettingsKnoValidation />,
          title: 'Настройки проверки КНО',
        },
        {
          route: ROUTE_ADMIN_CATALOGS_UPDATE,
          component: <AdminCatalogsUpdate />,
          title: 'Загрузка справочников',
        },
        {
          route: ROUTE_ADMIN_NEWS,
          component: <NewsTable />,
          title: 'Управление новостями',
        },
        ...(lawInterface === LawInterfacesEnum.FZ_248
          ? [
              {
                route: ROUTE_ADMIN_IMMEDIATE_TEST_PURCHASE,
                component: <ImmediateTestPurchaseForm />,
                title: 'Настройка незамедлительной контрольной закупки',
              },
            ]
          : []),
        {
          route: ROUTE_ADMIN_HIDE_FIELDS,
          component: <AdminHideFieldsFromSubjectForm />,
          title: 'Скрытие полей в форме сведения о КЛ',
        },
        {
          route: ROUTE_ADMIN_AUTOSAVE,
          component: <AdminAutosaveSettings />,
          title: 'Настройка автоматического сохранения паспортов ЕРКНМ',
        },
      ];
      const menuItemSystemInoperability: IMenuItem[] = [
        {
          route: ROUTE_ADMIN_INOPERABILITY,
          component: <SystemInoperabilityTable />,
          title: 'Неработоспособность системы',
        },
      ];
      const menuItemNewsletter: IMenuItem[] = [
        {
          route: ROUTE_ADMIN_NEWSLETTER,
          component: <ControlOfNewsLettersPage />,
          title: 'Контроль рассылок',
        },
      ];
      const menuItemEndCheckingErp: IMenuItem[] = [
        {
          route: ROUTE_ADMIN_END_CHECKING_ERP,
          component: <EndCheckingErp />,
          title: 'Настройка завершения проверок ЕРП',
        },
      ];

      const changeKnoInPlan: IMenuItem[] = [
        {
          route: ROUTE_ADMIN_CHANGE_KNO_IN_PLAN,
          component: <ChangeKnoInPlanForm />,
          title: 'Изменение КНО в плане',
        },
      ];
      const changeKnoInMeasure: IMenuItem[] = [
        {
          route: ROUTE_ADMIN_CHANGE_KNO_IN_MEASURE,
          component: <ChangeKnoInMeasureForm />,
          title: 'Изменение КНО в мероприятии',
        },
      ];
      const menuItemManageTestUsers: IMenuItem[] = [
        {
          route: ROUTE_ADMIN_MANGE_TEST_USER,
          component: <ManageTestUsersTable />,
          title: 'Управление Тестовыми УЗ',
        },
      ];

      const publishRules: IMenuItem[] = [
        {
          route: ROUTE_ADMIN_PUBLISH_RULES,
          component: <PublishRulesPage />,
          title: 'Настройка правил публикации',
        },
      ];

      const menuItemsSmevRequest: IMenuItem[] = [
        {
          route: ROUTE_ADMIN_SMEV,
          component: <SmevRequestForm />,
          title: 'Запросы СМЭВ',
        },
      ];

      return [
        notificationMenuItem,
        ...menuItemsAdmin,
        ...menuItemsBase,
        ...menuItemSystemInoperability,
        ...menuItemNewsletter,
        ...menuItemEndCheckingErp,
        ...changeKnoInPlan,
        ...changeKnoInMeasure,
        ...menuItemManageTestUsers,
        ...publishRules,
        ...menuItemsSmevRequest,
      ];
    }
    case UserRoleEnum.SUPERVISOR: {
      const menuItemsExec: IMenuItem[] = [
        {
          route: ROUTE_INFO,
          component: <UserInfo />,
          title: 'Общая информация',
          versionAvailable: [LawInterfacesEnum.FZ_248, LawInterfacesEnum.FZ_294],
        },
        notificationMenuItem,
        {
          route: ROUTE_CHECK_SHEETS,
          component: <CheckLists />,
          title: 'Проверочные листы',
          versionAvailable: [LawInterfacesEnum.FZ_294],
        },
        {
          route: ROUTE_REQUIREMENTS,
          component: <UserRequirements />,
          title: 'Обязательные требования',
          versionAvailable: [LawInterfacesEnum.FZ_294],
        },
      ];

      const menuEndItemsExec: IMenuItem[] = [
        {
          route: ROUTE_KNM_MASS_CANCELLATION,
          component: <PassportMassCancellation />,
          title: (
            <>
              Массовая отмена{' '}
              <span className={styles.NoWrapText}>
                мероприятий{isExistsGroupsForCancellation && <span className={styles.ErrorIcon}></span>}
              </span>
            </>
          ),
        },
      ];

      return [...menuItemsExec, ...menuItemsBase, ...menuEndItemsExec];
    }
    case UserRoleEnum.SUPERIOR_PROSECUTOR: {
      const menuItemsSupProc: IMenuItem[] = [
        {
          route: ROUTE_INFO,
          component: <UserInfo />,
          title: 'Общая информация',
          versionAvailable: [LawInterfacesEnum.FZ_248, LawInterfacesEnum.FZ_294],
        },
        notificationMenuItem,
        {
          route: ROUTE_CHECK_SHEETS,
          component: <CheckLists />,
          title: 'Проверочные листы',
          versionAvailable: [LawInterfacesEnum.FZ_294],
        },
        {
          route: ROUTE_REQUIREMENTS,
          component: <UserRequirements />,
          title: 'Обязательные требования',
          versionAvailable: [LawInterfacesEnum.FZ_294],
        },
      ];

      return [...menuItemsSupProc, ...menuItemsBase];
    }
    case UserRoleEnum.PROSECUTOR: {
      const menuItemsProc: IMenuItem[] = [
        {
          route: ROUTE_INFO,
          component: <UserInfo />,
          title: 'Общая информация',
          versionAvailable: [LawInterfacesEnum.FZ_248, LawInterfacesEnum.FZ_294],
        },
        notificationMenuItem,
        {
          route: ROUTE_CHECK_SHEETS,
          component: <CheckLists />,
          title: 'Проверочные листы',
          versionAvailable: [LawInterfacesEnum.FZ_294],
        },
        {
          route: ROUTE_REQUIREMENTS,
          component: <UserRequirements />,
          title: 'Обязательные требования',
          versionAvailable: [LawInterfacesEnum.FZ_294],
        },
      ];

      const menuEndItemsProc: IMenuItem[] = [
        {
          route: ROUTE_KNM_MASS_CANCELLATION,
          component: <PassportMassCancellation />,
          title: 'Массовая отмена мероприятий',
        },
        {
          route: ROUTE_KNM_MASS_DELETION,
          component: <PassportMassDeletion />,
          title: 'Массовое удаление мероприятий',
        },
      ];

      return [...menuItemsProc, ...menuItemsBase, ...menuEndItemsProc];
    }
    case UserRoleEnum.OMBUDSMAN: {
      const menuItemsOmb: IMenuItem[] = [
        {
          route: ROUTE_INFO,
          component: <UserInfo />,
          title: 'Общая информация',
          versionAvailable: [LawInterfacesEnum.FZ_248, LawInterfacesEnum.FZ_294],
        },
        notificationMenuItem,
      ];

      return [...menuItemsOmb, ...menuItemsBase];
    }
    default:
      return [...menuItemsBase];
  }
};

interface IPersonalAccountPageProps {
  user?: IUserInfo;
  role?: IUserRole;
  isExistsGroupsForCancellation: boolean;
  lawInterface?: LawInterfacesEnum;
}

const PersonalAccountWithoutStore = ({
  user,
  role,
  isExistsGroupsForCancellation,
  lawInterface,
}: IPersonalAccountPageProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const menuItems: IMenuItem[] = useMemo(
    () => renderRoutes(isExistsGroupsForCancellation, role?.sysname, lawInterface),
    [role, isExistsGroupsForCancellation, lawInterface],
  );

  const userName: string = useMemo(() => {
    const fio = {
      first: user?.userProfile.nameFirst,
      middle: user?.userProfile.nameMiddle,
      last: user?.userProfile.nameLast,
      login: user?.userAccount?.login ? `(${user?.userAccount?.login?.toUpperCase()})` : undefined,
    };

    return Object.values(fio)
      .filter(v => v !== undefined)
      .join(' ');
  }, [user?.userAccount?.login, user?.userProfile.nameFirst, user?.userProfile.nameLast, user?.userProfile.nameMiddle]);

  const header: JSX.Element = useMemo(
    () => (
      <h1 className={styles.Header}>
        <span className={styles.UserName}>{userName}</span>
        <small className={styles.UserRole}>{role ? jobMap[role.sysname] : ''}</small>
      </h1>
    ),
    [role, userName],
  );

  useEffect(() => {
    role?.sysname === UserRoleEnum.SUPERVISOR && dispatch(passportGroupGetIsExistsGroupsForCancellationAction());
  }, [role, dispatch]);

  return (
    <div className={styles.Container}>
      <ControlPanel menuItems={menuItems} header={header} />
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  user: getUserInfo(state),
  role: getUserRole(state),
  isExistsGroupsForCancellation: passportGroupIsExistsGroupsForCancellationSelector(state),
  lawInterface: getLawInterface(state),
});

export const PersonalAccount = connect(mapStateToProps)(PersonalAccountWithoutStore);
